import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { categories, getMoviesByCategory } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [activeCategory, setActiveCategory] = useState(currentSlug);

  useEffect(() => {
    setActiveCategory(currentSlug);
  }, [currentSlug]);

  const categoryMovies = getMoviesByCategory(activeCategory);
  const currentCategory = categories.find((c) => c.slug === activeCategory);

  return (
    <>
      <Navbar />
      <main className="pt-16 bg-background-950 min-h-screen">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory?.name || '全部' },
          ]}
        />

        {/* Category Tabs */}
        <section className="border-b border-background-800">
          <div className="w-full px-4 md:px-6 py-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer shrink-0 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-800 text-foreground-400 hover:bg-background-700 hover:text-foreground-300'
                  }`}
                  onClick={() => setActiveCategory(cat.slug)}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  <span>{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-8 md:py-10">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-lg md:text-xl font-heading font-bold text-foreground-100">
                {currentCategory?.name || '全部影视'}
                <span className="text-sm font-normal text-foreground-500 ml-2" style={{ fontFamily: 'var(--font-body)' }}>
                  共{categoryMovies.length}部
                </span>
              </h1>
            </div>

            {categoryMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {categoryMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} size="md" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-800 rounded-full">
                  <i className="ri-movie-2-line text-foreground-500 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-400 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i> 查看全部影视
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || '全部'} - 七七影院`,
            description: `七七影院${currentCategory?.name || '全部'}分类，提供海量${currentCategory?.name || ''}类高清免费电影与免费电视剧在线观看。`,
            url: typeof window !== 'undefined' ? window.location.href : '',
            isPartOf: {
              '@type': 'WebSite',
              name: '七七影院',
              url: typeof window !== 'undefined' ? window.location.origin : '',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
                { '@type': 'ListItem', position: 2, name: currentCategory?.name || '全部' },
              ],
            },
          }),
        }}
      />
    </>
  );
}